import Link from "next/link"
import Image from "next/image"
import { getStoryblokLink } from "utilities/getStoryblokLink"
import isInternalLink from "utilities/isInternalLink"
import getTelLink from "utilities/getTelLink"
import CallToAction from "./CallToAction"
import Facebook from "public/assets/social/facebook.svg"
import Instagram from "public/assets/social/instagram.svg"
import LinkedIn from "public/assets/social/linkedin.svg"
import Twitter from "public/assets/social/twitter.svg"
import Snapchat from "public/assets/social/snapchat.svg"
import TikTok from "public/assets/social/tiktok.svg"
import Phone from "public/assets/social/phone.svg"
import Printer from "public/assets/social/printer.svg"
import Logo from "public/assets/logo.svg"

export default function Footer({ globals }) {
  const linkStyles =
    "flex gap-4 items-center justify-center lg:justify-start font-secondary font-medium text-m1 text-stone-light hover:underline hover:text-moss-light transition-all duration-200 mb-2 mx-auto lg:mx-0"
  const menuHeadings = "text-center lg:text-left text-m3 font-medium pb-4"

  return (
    <footer className="main-footer relative z-50">
      <div className="bg-evergreen px-6 text-stone-light">
        <div className="mx-auto max-w-screen-xl justify-items-center py-12 md:grid md:grid-cols-4 md:gap-y-8 lg:justify-items-start">
          <div className="w-full border-b border-stone-light pb-4 md:col-span-4 md:w-auto md:border-none md:pb-0 lg:col-span-1">
            <Link href="/" className="mx-auto block w-max pb-6 md:mx-0">
              <Logo className="h-min w-[225px]" alt="OneQuest Health" />
            </Link>
            <h3 className={menuHeadings}>Follow Us</h3>
            <div className="mx-auto flex w-max gap-4 md:mx-0">
              <SocialIcon href={globals?.socialMedia?.Twitter} icon="twitter" />
              <SocialIcon href={globals?.socialMedia?.Facebook} icon="facebook" />
              <SocialIcon href={globals?.socialMedia?.Instagram} icon="instagram" />
              <SocialIcon href={globals?.socialMedia?.LinkedIn} icon="linkedin" />
              <SocialIcon href={globals?.socialMedia?.TikTok} icon="tiktok" />
              <SocialIcon href={globals?.socialMedia?.Snapchat} icon="snapchat" />
            </div>
          </div>

          <div className="my-4 md:my-0">
            <h3 className={menuHeadings}>Contact</h3>
            <a href={getTelLink(globals?.phoneNumbers?.Main)} className={linkStyles}>
              <Phone /> {globals?.phoneNumbers?.Main}
            </a>
            <a href={getTelLink(globals?.phoneNumbers?.Fax)} className={linkStyles}>
              <Printer /> {globals?.phoneNumbers?.Fax}
            </a>
            <div className="mx-auto text-center lg:mt-8 lg:text-left">
              <CallToAction
                style="small"
                href="https://visitor.r20.constantcontact.com/d.jsp?llr=5qft8oeab&amp;p=oi&amp;m=5qft8oeab&amp;sit=jiwdsexfb&amp;f=ace6f9ff-0cb1-4a5f-a221-ff9e13c10c37"
              >
                Newsletter Signup
              </CallToAction>
            </div>
          </div>

          <nav className="w-full border-y border-stone-light py-4 md:w-auto md:border-none md:py-0">
            <h3 className={menuHeadings}>{globals?.footer?.name}</h3>
            {globals?.footer?.content?.menu_items.map((item) => {
              const href = getStoryblokLink(item.link)
              return (
                <Link
                  href={href}
                  key={item.label}
                  className={linkStyles}
                  target={isInternalLink(href) ? "_self" : "_blank"}
                >
                  {item.label}
                </Link>
              )
            })}
          </nav>

          <div className="grid w-full grid-cols-2 grid-rows-[repeat(2,max-content)] items-center justify-items-center gap-6 px-5 pt-8 md:col-span-2 md:pt-0 lg:col-span-1 lg:gap-11 lg:px-0">
            {/* These small WebP logos can be served directly without the IPX optimizer. */}
            <Image
              src="/assets/footer-logos/hrc-acaf.webp"
              alt="Human Rights Campaign Foundation"
              width={150}
              height={36}
              className="w-full"
              unoptimized
            />
            <Image
              src="/assets/footer-logos/sanctuary-institute.webp"
              alt="Sanctuary Institute"
              width={150}
              height={39}
              className="w-full"
              unoptimized
            />
            <Image
              src="/assets/footer-logos/coa.webp"
              alt="Council on Accreditation"
              width={150}
              height={53}
              className="w-full"
              unoptimized
            />
            <Image
              src="/assets/footer-logos/ellequate.webp"
              alt="Ellequate"
              width={150}
              height={33}
              className="w-full"
              unoptimized
            />
          </div>
        </div>
      </div>

      {/* Disclaimers & Copyright */}
      <div className="bg-stone px-6">
        <div className="mx-auto flex max-w-screen-xl flex-col items-center gap-y-4 py-6 text-s1 text-teal-dark lg:flex-row lg:py-3">
          <div className="grow text-center lg:text-left">
            © {new Date().getFullYear()} OneQuest Health. All rights reserved.
          </div>
          <div className="flex divide-x divide-teal-dark">
            <Link href="/privacy-policy" className="pr-4 hover:underline">
              Privacy Notice
            </Link>
            <Link href="/hipaa-notice" className="pl-4 hover:underline">
              HIPAA Notice
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

function SocialIcon({ href, icon }) {
  const Icons = {
    facebook: <Facebook />,
    instagram: <Instagram />,
    twitter: <Twitter />,
    linkedin: <LinkedIn />,
    snapchat: <Snapchat />,
    tiktok: <TikTok />,
  }
  const Icon = () => Icons?.[icon] ?? null

  return Icon && href ? (
    <Link
      href={href}
      target="_blank"
      className="mx-auto mb-2 flex items-center justify-center gap-4 font-secondary text-m1 font-medium text-stone-light transition-all duration-200 hover:text-moss-light lg:mx-0 lg:justify-start"
    >
      <Icon />
    </Link>
  ) : null
}
